/*
 * Book York | Property gallery v3.0
 * Native, dependency-free viewer. All DOM and styles are accommodation-only.
 * Images are decoded off-screen; request tokens prevent stale asynchronous paints.
 * Booking behaviour and the sticky-height safeguard are retained from Refined v2.
 */
(() => {
  'use strict';

  const GLOBAL_KEY = '__bookYorkPropertyGalleryV3';
  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scan();
    return;
  }
  const instances = new WeakMap();
  let serial = 0;
  let activeViewer = null;
  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const paths = {
    close: 'M6 6l12 12M18 6 6 18',
    previous: 'm14 5-7 7 7 7',
    next: 'm10 5 7 7-7 7',
    grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
    zoom: 'M10.5 6.5v8m-4-4h8M16 16l5 5M18 10.5a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0',
    fit: 'M6.5 10.5h8M16 16l5 5M18 10.5a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0',
    external: 'M14 4h6v6m0-6L10 14M10 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5'
  };
  function icon(name) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '22');
    svg.setAttribute('height', '22');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', paths[name]);
    svg.append(path);
    return svg;
  }
  function control(className, label, symbol) {
    const button = make('button', className);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.title = label;
    button.append(icon(symbol));
    return button;
  }
  function safeURL(value) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, document.baseURI);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  }

  function initialise(root) {
    if (instances.has(root) || root.dataset.byGalleryBound === '1') return;
    root.dataset.byGalleryBound = '1';
    const controller = new AbortController();
    const { signal } = controller;
    const on = (node, type, handler, options = {}) => node.addEventListener(type, handler, { ...options, signal });
    const cache = new Map();
    const MAX_CACHE = 10;
    const LOAD_TIMEOUT = 20000;
    let dialog, viewport, canvas, stage, caption, counter, original, filmstrip, overview;
    let status, feedback, feedbackText, retry, zoomButton, gridButton, closeButton, previous, next;
    let imageData = [], thumbButtons = [], gridButtons = [];
    let active = 0, ticket = 0, displayedQuality = 0;
    let fullReady = false, zoomed = false, overviewOpen = false, isOpen = false;
    let opener = null, scrollState = null, resizeObserver = null;
    let loadingTimer = 0, prefetchTimer = 0, resizeTimer = 0, announceTimer = 0;
    let gesture = null, suppressClickUntil = 0;
    const touches = new Set();
    let multiTouch = false;
    let titleText = '';

    const wrap = index => ((Math.trunc(index) % imageData.length) + imageData.length) % imageData.length;
    const dataSaver = () => {
      const connection = navigator.connection;
      return !!(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));
    };
    function announce(text) {
      window.clearTimeout(announceTimer);
      announceTimer = window.setTimeout(() => {
        if (isOpen) status.textContent = text;
      }, 120);
    }
    function cancelEntry(url, entry) {
      if (entry.state === 'pending') entry.cancel();
      if (cache.get(url) === entry) cache.delete(url);
    }
    function cancelUnneeded(keep = new Set()) {
      cache.forEach((entry, url) => {
        if (entry.state === 'pending' && !keep.has(url)) cancelEntry(url, entry);
      });
    }
    function trimCache() {
      if (cache.size <= MAX_CACHE) return;
      const mountedURL = canvas?.querySelector('img')?.src;
      for (const [url, entry] of cache) {
        if (cache.size <= MAX_CACHE) break;
        if (url !== mountedURL && entry.state !== 'pending') cache.delete(url);
      }
    }
    function loadImage(url, priority = 'high') {
      if (cache.has(url)) {
        const entry = cache.get(url);
        cache.delete(url);
        cache.set(url, entry);
        if (priority === 'high') entry.image.setAttribute('fetchpriority', 'high');
        return entry.promise;
      }
      const image = new Image();
      image.decoding = 'async';
      image.loading = 'eager';
      image.draggable = false;
      image.setAttribute('fetchpriority', priority);
      const entry = { image, state: 'pending', cancel: null, promise: null };
      entry.promise = new Promise((resolve, reject) => {
        let settled = false;
        const timer = window.setTimeout(() => finish(new Error('Image request timed out')), LOAD_TIMEOUT);
        function finish(error) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          image.onload = image.onerror = null;
          if (error) {
            entry.state = 'failed';
            image.removeAttribute('src');
            if (cache.get(url) === entry) cache.delete(url);
            reject(error);
          } else {
            entry.state = 'ready';
            resolve(image);
            trimCache();
          }
        }
        entry.cancel = () => finish(new DOMException('Superseded image request', 'AbortError'));
        image.onerror = () => finish(new Error('Image could not be loaded'));
        image.onload = async () => {
          // Safari can reject decode() for an otherwise valid image. Never discard
          // a successfully loaded bitmap solely because that optional step rejects.
          try { if (image.decode) await image.decode(); } catch (_) { /* Check below. */ }
          finish(image.complete && image.naturalWidth > 0 ? null : new Error('Image could not be decoded'));
        };
        image.src = url;
      });
      cache.set(url, entry);
      // Every caller also handles errors. This guard covers lifecycle cancellation.
      entry.promise.catch(() => {});
      return entry.promise;
    }
    function displayURL(photo) {
      const ratio = photo.width && photo.height ? photo.width / photo.height : 1.5;
      const width = Math.min(viewport.clientWidth || window.innerWidth, (viewport.clientHeight || window.innerHeight) * ratio);
      const density = Math.min(window.devicePixelRatio || 1, dataSaver() ? 1 : 2);
      return photo.small && width * density <= 960 ? photo.small : photo.src;
    }
    function updateCounter() {
      counter.textContent = `${String(active + 1).padStart(2, '0')} / ${String(imageData.length).padStart(2, '0')}`;
      counter.setAttribute('aria-label', `Photo ${active + 1} of ${imageData.length}`);
    }
    function centreThumb() {
      const thumb = thumbButtons[active];
      if (!thumb || overviewOpen) return;
      const stripRect = filmstrip.getBoundingClientRect();
      const rect = thumb.getBoundingClientRect();
      // Scroll this strip only. scrollIntoView() can also move the whole page.
      filmstrip.scrollLeft += rect.left - stripRect.left - (filmstrip.clientWidth - rect.width) / 2;
    }
    function refreshSelection() {
      thumbButtons.forEach((button, index) => {
        button.setAttribute('aria-current', String(index === active));
        button.tabIndex = index === active ? 0 : -1;
      });
      gridButtons.forEach((button, index) => {
        button.setAttribute('aria-current', String(index === active));
        button.tabIndex = index === active ? 0 : -1;
      });
      [wrap(active - 1), active, wrap(active + 1)].forEach(index => {
        const image = thumbButtons[index]?.querySelector('img');
        if (image) image.loading = 'eager';
      });
      centreThumb();
    }
    function showFeedback(kind, message) {
      feedback.dataset.kind = kind;
      feedbackText.textContent = message;
      retry.hidden = kind !== 'error';
      feedback.hidden = !message;
    }
    function setZoom(value, point) {
      zoomed = !!value && fullReady && !overviewOpen;
      dialog.classList.toggle('is-zoomed', zoomed);
      zoomButton.setAttribute('aria-pressed', String(zoomed));
      zoomButton.setAttribute('aria-label', zoomed ? 'Zoom out to fit photo' : 'Zoom in on photo');
      zoomButton.title = zoomed ? 'Zoom out to fit photo' : 'Zoom in on photo';
      zoomButton.replaceChildren(icon(zoomed ? 'fit' : 'zoom'));
      canvas.style.removeProperty('width');
      canvas.style.removeProperty('height');
      const image = canvas.querySelector('img');
      if (image) {
        image.style.removeProperty('width');
        image.style.removeProperty('height');
      }
      viewport.scrollLeft = viewport.scrollTop = 0;
      if (!zoomed || !image) return;
      const photo = imageData[active];
      const ratio = photo.width && photo.height ? photo.width / photo.height : image.naturalWidth / image.naturalHeight;
      const fittedWidth = Math.min(viewport.clientWidth, viewport.clientHeight * ratio);
      const fittedHeight = fittedWidth / ratio;
      const width = fittedWidth * 2;
      const height = fittedHeight * 2;
      const canvasWidth = Math.max(viewport.clientWidth, width);
      const canvasHeight = Math.max(viewport.clientHeight, height);
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      image.style.width = `${width}px`;
      image.style.height = `${height}px`;
      const x = point ? Math.max(0, Math.min(1, (point.x - (viewport.clientWidth - fittedWidth) / 2) / fittedWidth)) : 0.5;
      const y = point ? Math.max(0, Math.min(1, (point.y - (viewport.clientHeight - fittedHeight) / 2) / fittedHeight)) : 0.5;
      viewport.scrollLeft = (canvasWidth - width) / 2 + width * x - viewport.clientWidth / 2;
      viewport.scrollTop = (canvasHeight - height) / 2 + height * y - viewport.clientHeight / 2;
      // A mobile fit-to-screen image can be smaller. Fetch the larger existing
      // Shopify rendition only on explicit zoom, not for every phone slide.
      const expectedTicket = ticket;
      loadImage(photo.src).then(sharp => {
        if (!isOpen || !zoomed || ticket !== expectedTicket || canvas.firstElementChild === sharp) return;
        const old = canvas.firstElementChild;
        sharp.className = 'by-property-dialog__image';
        sharp.alt = photo.alt;
        sharp.dataset.photoIndex = String(active);
        sharp.style.width = old.style.width;
        sharp.style.height = old.style.height;
        if (photo.width) sharp.width = photo.width;
        if (photo.height) sharp.height = photo.height;
        canvas.replaceChildren(sharp);
      }).catch(() => { /* Keep the usable fit-resolution image, with its zoom. */ });
    }
    function paint(image, index, expectedTicket, quality) {
      if (!isOpen || ticket !== expectedTicket || active !== index || quality < displayedQuality) return false;
      displayedQuality = quality;
      image.className = 'by-property-dialog__image';
      image.alt = imageData[index].alt;
      image.draggable = false;
      image.dataset.photoIndex = String(index);
      image.style.removeProperty('width');
      image.style.removeProperty('height');
      if (imageData[index].width) image.width = imageData[index].width;
      if (imageData[index].height) image.height = imageData[index].height;
      canvas.replaceChildren(image);
      dialog.dataset.displayedIndex = String(index);
      dialog.classList.toggle('has-preview', quality < 2);
      return true;
    }
    function prefetchNeighbours() {
      window.clearTimeout(prefetchTimer);
      if (dataSaver() || imageData.length < 2) return;
      const expectedTicket = ticket;
      prefetchTimer = window.setTimeout(() => {
        if (!isOpen || ticket !== expectedTicket || overviewOpen) return;
        // Exactly the next/previous view, not all twenty large photographs.
        new Set([wrap(active + 1), wrap(active - 1)]).forEach(index => {
          loadImage(displayURL(imageData[index]), 'low').catch(() => {});
        });
      }, 120);
    }
    function render(index) {
      if (!imageData.length || !isOpen) return;
      active = wrap(index);
      const expectedTicket = ++ticket;
      const expectedIndex = active;
      const photo = imageData[active];
      const targetURL = displayURL(photo);
      window.clearTimeout(loadingTimer);
      window.clearTimeout(prefetchTimer);
      setZoom(false);
      fullReady = false;
      displayedQuality = 0;
      zoomButton.disabled = true;
      dialog.dataset.activeIndex = String(active);
      dialog.dataset.state = 'loading';
      delete dialog.dataset.displayedIndex;
      caption.textContent = photo.caption;
      caption.title = photo.caption;
      original.href = photo.src;
      original.setAttribute('aria-label', `Open full-size photo ${active + 1} in a new tab`);
      viewport.setAttribute('aria-label', `Photo ${active + 1} of ${imageData.length}: ${photo.caption}`);
      canvas.replaceChildren(); // Never leave the previous photograph under a new caption.
      stage.setAttribute('aria-busy', 'true');
      showFeedback('', '');
      updateCounter();
      refreshSelection();
      cancelUnneeded(new Set([targetURL, photo.thumb]));
      const ready = cache.get(targetURL);
      const finish = image => {
        if (!paint(image, expectedIndex, expectedTicket, 2)) return;
        window.clearTimeout(loadingTimer);
        fullReady = true;
        zoomButton.disabled = overviewOpen;
        stage.setAttribute('aria-busy', 'false');
        dialog.dataset.state = 'ready';
        showFeedback('', '');
        announce(`Photo ${active + 1} of ${imageData.length}. ${photo.caption}`);
        prefetchNeighbours();
      };
      if (ready?.state === 'ready') {
        finish(ready.image);
        return;
      }
      // Use a cached page tile or thumbnail as an honest, immediate preview.
      // Its own index travels with the image; old requests cannot overwrite it.
      const pageImage = root.querySelector(`[data-by-open-photo="${active}"] img`);
      const thumbImage = thumbButtons[active]?.querySelector('img');
      const preview = [pageImage, thumbImage].find(image => image?.complete && image.naturalWidth > 0);
      if (preview) {
        const clone = preview.cloneNode(false);
        clone.removeAttribute('srcset');
        clone.removeAttribute('sizes');
        clone.removeAttribute('id');
        clone.src = preview.currentSrc || preview.src;
        clone.loading = 'eager';
        paint(clone, expectedIndex, expectedTicket, 1);
      } else if (photo.thumb) {
        loadImage(photo.thumb, 'auto').then(image => paint(image, expectedIndex, expectedTicket, 1)).catch(() => {});
      }
      loadingTimer = window.setTimeout(() => {
        if (isOpen && ticket === expectedTicket) showFeedback('loading', displayedQuality ? 'Loading full resolution' : 'Loading photograph');
      }, displayedQuality ? 220 : 80);
      loadImage(targetURL).then(finish).catch(error => {
        if (!isOpen || ticket !== expectedTicket || error.name === 'AbortError') return;
        window.clearTimeout(loadingTimer);
        stage.setAttribute('aria-busy', 'false');
        dialog.dataset.state = 'error';
        const message = displayedQuality ? 'Preview only. Full resolution could not load.' : 'This photo could not load.';
        showFeedback('error', message);
        announce(`${message} Retry or choose another photo.`);
      });
    }
    function move(amount) {
      if (imageData.length < 2) return;
      const focusIsThumb = thumbButtons.includes(document.activeElement);
      render(active + amount);
      if (focusIsThumb) thumbButtons[active].focus({ preventScroll: true });
    }
    function buildOverview() {
      if (gridButtons.length) return;
      const grid = make('div', 'by-property-dialog__overview-grid');
      imageData.forEach((photo, index) => {
        const button = make('button', 'by-property-dialog__overview-photo');
        button.type = 'button';
        button.dataset.photoIndex = String(index);
        button.setAttribute('aria-label', `View photo ${index + 1}: ${photo.caption}`);
        const image = make('img');
        image.alt = '';
        image.width = photo.width || 960;
        image.height = photo.height || 640;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.src = photo.small || photo.src;
        const label = make('span', 'by-property-dialog__overview-label');
        label.append(make('span', 'by-property-dialog__overview-number', String(index + 1).padStart(2, '0')), make('span', '', photo.caption));
        button.append(image, label);
        on(button, 'click', () => {
          setOverview(false, false);
          render(index);
          viewport.focus({ preventScroll: true });
        });
        on(button, 'keydown', event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
          const offsets = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns };
          let target = index;
          if (event.key in offsets) target = Math.max(0, Math.min(imageData.length - 1, index + offsets[event.key]));
          else if (event.key === 'Home') target = 0;
          else if (event.key === 'End') target = imageData.length - 1;
          else return;
          event.preventDefault();
          event.stopPropagation();
          gridButtons.forEach((item, i) => { item.tabIndex = i === target ? 0 : -1; });
          gridButtons[target].focus();
        });
        grid.append(button);
        gridButtons.push(button);
      });
      overview.append(grid);
    }
    function setOverview(value, restoreFocus = true) {
      overviewOpen = !!value;
      setZoom(false);
      dialog.classList.toggle('is-overview', overviewOpen);
      overview.hidden = !overviewOpen;
      gridButton.setAttribute('aria-expanded', String(overviewOpen));
      gridButton.setAttribute('aria-label', overviewOpen ? 'Back to selected photo' : 'Browse all photos');
      gridButton.title = overviewOpen ? 'Back to selected photo' : 'Browse all photos';
      gridButton.replaceChildren(icon(overviewOpen ? 'previous' : 'grid'), make('span', 'by-property-dialog__button-text', overviewOpen ? 'Back to photo' : 'All photos'));
      zoomButton.disabled = overviewOpen || !fullReady;
      if (overviewOpen) {
        window.clearTimeout(prefetchTimer);
        buildOverview();
        refreshSelection();
        gridButtons[active].focus();
      } else {
        centreThumb();
        if (restoreFocus) gridButton.focus({ preventScroll: true });
        if (fullReady) prefetchNeighbours();
      }
    }
    function lockScroll() {
      if (scrollState) return;
      const body = document.body;
      const names = ['position', 'top', 'left', 'right', 'width', 'padding-right'];
      const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      scrollState = {
        x: window.scrollX, y: window.scrollY,
        hadClass: document.documentElement.classList.contains('by-property-gallery-open'),
        styles: names.map(name => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)])
      };
      if (gap) body.style.setProperty('padding-right', `${parseFloat(getComputedStyle(body).paddingRight) + gap}px`);
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `${-scrollState.y}px`);
      body.style.setProperty('left', `${-scrollState.x}px`);
      body.style.setProperty('right', '0');
      body.style.setProperty('width', '100%');
      document.documentElement.classList.add('by-property-gallery-open');
    }
    function unlockScroll() {
      if (!scrollState) return;
      const state = scrollState;
      scrollState = null;
      state.styles.forEach(([name, value, priority]) => {
        if (value) document.body.style.setProperty(name, value, priority);
        else document.body.style.removeProperty(name);
      });
      if (!state.hadClass) document.documentElement.classList.remove('by-property-gallery-open');
      const html = document.documentElement;
      const behaviour = html.style.getPropertyValue('scroll-behavior');
      const priority = html.style.getPropertyPriority('scroll-behavior');
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(state.x, state.y);
      if (behaviour) html.style.setProperty('scroll-behavior', behaviour, priority);
      else html.style.removeProperty('scroll-behavior');
    }
    function finishClose() {
      if (!isOpen) return;
      isOpen = false;
      ++ticket;
      [loadingTimer, prefetchTimer, resizeTimer, announceTimer].forEach(timer => window.clearTimeout(timer));
      cancelUnneeded();
      setZoom(false);
      canvas.replaceChildren();
      touches.clear();
      gesture = null;
      multiTouch = false;
      unlockScroll();
      if (activeViewer === closeViewer) activeViewer = null;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    }
    function closeViewer() {
      if (dialog?.open) dialog.close();
      finishClose();
    }
    function syncViewport() {
      if (!dialog) return;
      const visual = window.visualViewport;
      const browserZoom = !!visual && visual.scale > 1.02;
      dialog.classList.toggle('is-browser-zoomed', browserZoom);
      if (!browserZoom) dialog.style.setProperty('--by-viewer-height', `${Math.round(visual?.height || window.innerHeight)}px`);
      if (!isOpen) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (!isOpen) return;
        if (zoomed) setZoom(true);
        else if (fullReady && !overviewOpen) {
          const photo = imageData[active];
          // Resize upgrades only. Do not replace a sharp image with a smaller one.
          if (displayURL(photo) === photo.src && canvas.firstElementChild?.src !== photo.src) {
            const expectedTicket = ticket;
            loadImage(photo.src).then(image => { if (!zoomed) paint(image, active, expectedTicket, 2); }).catch(() => {});
          }
        }
        centreThumb();
      }, 160);
    }
    function bindGestures() {
      on(viewport, 'pointerdown', event => {
        if (event.pointerType !== 'mouse') {
          touches.add(event.pointerId);
          if (touches.size > 1) { multiTouch = true; gesture = null; return; }
        }
        if (event.button !== 0 || (window.visualViewport?.scale || 1) > 1.02) return;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop, moved: false, time: performance.now(), type: event.pointerType };
        if (zoomed && event.pointerType === 'mouse') {
          viewport.setPointerCapture(event.pointerId);
          event.preventDefault();
          dialog.classList.add('is-dragging');
        }
      });
      on(viewport, 'pointermove', event => {
        if (!gesture || gesture.id !== event.pointerId || multiTouch) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (Math.abs(dx) + Math.abs(dy) > 8) gesture.moved = true;
        if (zoomed && gesture.type === 'mouse') {
          viewport.scrollLeft = gesture.left - dx;
          viewport.scrollTop = gesture.top - dy;
        }
      }, { passive: true });
      const endGesture = event => {
        touches.delete(event.pointerId);
        const start = gesture;
        if (start?.id === event.pointerId) {
          if (event.type === 'pointerup' && !zoomed && !multiTouch && start.type !== 'mouse') {
            const dx = event.clientX - start.x;
            const dy = event.clientY - start.y;
            if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.35 && performance.now() - start.time < 1200) move(dx < 0 ? 1 : -1);
          }
          if (start.moved) suppressClickUntil = performance.now() + 350;
          gesture = null;
        }
        if (!touches.size) multiTouch = false;
        dialog.classList.remove('is-dragging');
        if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      };
      on(viewport, 'pointerup', endGesture);
      on(viewport, 'pointercancel', endGesture);
      on(viewport, 'lostpointercapture', event => {
        if (gesture?.id === event.pointerId) gesture = null;
        dialog.classList.remove('is-dragging');
      });
      on(viewport, 'click', event => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || performance.now() < suppressClickUntil || !fullReady) return;
        const rect = viewport.getBoundingClientRect();
        setZoom(!zoomed, { x: event.clientX - rect.left, y: event.clientY - rect.top });
      });
    }
    function buildViewer() {
      if (dialog) return true;
      const manifest = root.querySelector('[data-by-gallery-data]');
      if (!manifest || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return false;
      let data;
      try { data = JSON.parse(manifest.textContent); } catch (_) { return false; }
      if (!data || !Array.isArray(data.images)) return false;
      // Do not filter out manifest slots: the existing page links use their
      // original indexes. On invalid data leave the native image links intact.
      imageData = data.images.map(photo => {
        if (!photo || typeof photo !== 'object') return { src: '' };
        const alt = typeof photo.alt === 'string' ? photo.alt : 'Property photograph';
        const text = typeof photo.caption === 'string' && photo.caption.trim() ? photo.caption.trim() : alt;
        return {
          src: safeURL(photo.src), small: safeURL(photo.small), thumb: safeURL(photo.thumb), alt,
          caption: text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Property photograph',
          width: Number(photo.width) > 0 ? Number(photo.width) : 0,
          height: Number(photo.height) > 0 ? Number(photo.height) : 0
        };
      });
      if (!imageData.length || imageData.some(photo => !photo.src)) return false;
      titleText = typeof data.title === 'string' ? data.title : 'Property photographs';
      const id = `by-property-viewer-${++serial}`;
      dialog = make('dialog', 'by-property-dialog');
      dialog.dataset.version = '3.0';
      dialog.setAttribute('aria-labelledby', `${id}-title`);
      dialog.setAttribute('aria-modal', 'true');
      const header = make('header', 'by-property-dialog__header');
      const heading = make('div', 'by-property-dialog__heading');
      heading.append(make('span', 'by-property-dialog__eyebrow', 'Book York · Photo collection'));
      const title = make('h2', 'by-property-dialog__title', typeof data.shortTitle === 'string' ? data.shortTitle : titleText);
      title.id = `${id}-title`;
      title.title = titleText;
      heading.append(title);
      const tools = make('div', 'by-property-dialog__tools');
      gridButton = control('by-property-dialog__grid-button', 'Browse all photos', 'grid');
      gridButton.append(make('span', 'by-property-dialog__button-text', 'All photos'));
      gridButton.setAttribute('aria-controls', `${id}-overview`);
      gridButton.setAttribute('aria-expanded', 'false');
      zoomButton = control('by-property-dialog__zoom', 'Zoom in on photo', 'zoom');
      zoomButton.setAttribute('aria-pressed', 'false');
      closeButton = control('by-property-dialog__close', 'Close photo gallery', 'close');
      closeButton.autofocus = true;
      tools.append(gridButton, zoomButton, closeButton);
      header.append(heading, tools);
      stage = make('div', 'by-property-dialog__stage');
      viewport = make('div', 'by-property-dialog__viewport');
      viewport.tabIndex = -1;
      viewport.setAttribute('role', 'group');
      canvas = make('div', 'by-property-dialog__canvas');
      viewport.append(canvas);
      previous = control('by-property-dialog__previous', 'Previous photo', 'previous');
      next = control('by-property-dialog__next', 'Next photo', 'next');
      previous.hidden = next.hidden = imageData.length < 2;
      feedback = make('div', 'by-property-dialog__feedback');
      feedback.hidden = true;
      const spinner = make('span', 'by-property-dialog__spinner');
      spinner.setAttribute('aria-hidden', 'true');
      feedbackText = make('span');
      retry = make('button', 'by-property-dialog__retry', 'Retry');
      retry.type = 'button';
      retry.hidden = true;
      feedback.append(spinner, feedbackText, retry);
      const zoomHint = make('span', 'by-property-dialog__zoom-hint', 'Drag to explore · Zoom out to fit');
      zoomHint.setAttribute('aria-hidden', 'true');
      stage.append(previous, viewport, next, feedback, zoomHint);
      const footer = make('div', 'by-property-dialog__footer');
      caption = make('p', 'by-property-dialog__caption');
      counter = make('span', 'by-property-dialog__counter');
      original = make('a', 'by-property-dialog__original');
      original.append(icon('external'));
      original.target = '_blank';
      original.rel = 'noopener noreferrer';
      original.title = 'Open full-size photo in a new tab';
      footer.append(caption, counter, original);
      filmstrip = make('div', 'by-property-dialog__thumbs');
      filmstrip.setAttribute('role', 'group');
      filmstrip.setAttribute('aria-label', 'Choose a photograph');
      imageData.forEach((photo, index) => {
        const thumb = make('button', 'by-property-dialog__thumb');
        thumb.type = 'button';
        thumb.setAttribute('aria-label', `View photo ${index + 1}: ${photo.caption}`);
        const image = make('img');
        image.width = 144;
        image.height = 96;
        image.alt = '';
        // The viewer itself is on demand. Tiny thumbnails can load together,
        // including Safari's otherwise unreliable lazy images in the top layer.
        image.loading = 'eager';
        image.decoding = 'async';
        image.src = photo.thumb || photo.small || photo.src;
        thumb.append(image);
        on(thumb, 'click', () => render(index));
        filmstrip.append(thumb);
        thumbButtons.push(thumb);
      });
      filmstrip.hidden = imageData.length < 2;
      overview = make('section', 'by-property-dialog__overview');
      overview.id = `${id}-overview`;
      overview.setAttribute('aria-label', 'All property photographs');
      overview.hidden = true;
      status = make('div', 'by-property-dialog__sr-only');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      dialog.append(header, stage, footer, filmstrip, overview, status);
      // Body mounting isolates the viewer from section font/paragraph rules.
      document.body.append(dialog);
      on(closeButton, 'click', closeViewer);
      on(gridButton, 'click', () => setOverview(!overviewOpen));
      on(zoomButton, 'click', () => setZoom(!zoomed));
      on(previous, 'click', () => move(-1));
      on(next, 'click', () => move(1));
      on(retry, 'click', () => render(active));
      on(dialog, 'close', () => { if (!dialog.open) finishClose(); });
      on(dialog, 'cancel', event => {
        if (zoomed) { event.preventDefault(); setZoom(false); }
        else if (overviewOpen) { event.preventDefault(); setOverview(false); }
        else { event.preventDefault(); closeViewer(); }
      });
      let backdropDown = false;
      const outsideDialog = event => {
        const rect = dialog.getBoundingClientRect();
        return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      };
      on(dialog, 'pointerdown', event => { backdropDown = event.target === dialog && outsideDialog(event); });
      on(dialog, 'click', event => {
        if (backdropDown && event.target === dialog && outsideDialog(event)) closeViewer();
        backdropDown = false;
      });
      on(dialog, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'Tab') {
          const controls = [...dialog.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), a[href]')].filter(element => element.getClientRects().length);
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
            event.preventDefault(); last?.focus({ preventScroll: true });
          } else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement))) {
            event.preventDefault(); first?.focus({ preventScroll: true });
          }
          return;
        }
        if (overviewOpen) return;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (event.key === 'Home' || event.key === 'End') render(event.key === 'Home' ? 0 : imageData.length - 1);
          else move(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault(); setZoom(true);
        } else if (event.key === '-') {
          event.preventDefault(); setZoom(false);
        }
      });
      bindGestures();
      on(window, 'resize', syncViewport, { passive: true });
      if (window.visualViewport) on(window.visualViewport, 'resize', syncViewport, { passive: true });
      syncViewport();
      return true;
    }

    on(root, 'click', event => {
      const link = event.target.closest('[data-by-open-photo]');
      if (link && root.contains(link)) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        if (!buildViewer()) return; // Real image links still work without JavaScript/dialog support.
        if (activeViewer && activeViewer !== closeViewer) activeViewer();
        opener = link;
        if (!dialog.open) {
          lockScroll();
          try { dialog.showModal(); } catch (_) { unlockScroll(); return; }
        }
        event.preventDefault();
        isOpen = true;
        activeViewer = closeViewer;
        setOverview(false, false);
        syncViewport();
        const requested = Number(link.dataset.byOpenPhoto);
        render(Number.isFinite(requested) ? requested : 0);
        closeButton.focus({ preventScroll: true });
        return;
      }
      // The existing booking script owns the action; suppress only the fallback anchor jump.
      if (event.target.closest('a[data-book-york-booking-jump]') && root.querySelector('[data-booking-bound="1"]')) event.preventDefault();
    });
    const sticky = root.querySelector('[data-by-sticky-booking]');
    const updateSticky = () => {
      if (!sticky) return;
      sticky.classList.toggle('by-property-booking--tall', sticky.offsetHeight > window.innerHeight - 130);
    };
    if (sticky && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateSticky);
      resizeObserver.observe(sticky);
      on(window, 'resize', updateSticky, { passive: true });
      updateSticky();
    }
    instances.set(root, () => {
      closeViewer();
      dialog?.remove();
      resizeObserver?.disconnect();
      controller.abort();
      cache.forEach((entry, url) => cancelEntry(url, entry));
      cache.clear();
      instances.delete(root);
      delete root.dataset.byGalleryBound;
    });
  }
  function roots(scope) {
    const result = [...scope.querySelectorAll('[data-by-property]')];
    if (scope.matches?.('[data-by-property]')) result.unshift(scope);
    return result;
  }
  function scan(scope = document) { roots(scope).forEach(initialise); }
  window[GLOBAL_KEY] = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => roots(event.target).forEach(root => instances.get(root)?.()));
})();
